import { distinct, ownKeys, ownEntries } from '#library/primitive.library.js';
import { asType, getType } from '#library/type.library.js';
import { isEmpty, isFunction, isPrimitive, isReference } from '#library/assertion.library.js';
/**
 * Mutates an object or array by deleting properties that match specified primitive types.
 *
 * @param obj - The object or array to mutate
 * @param types - The primitive types to exclude (e.g., 'Function', 'String')
 * @returns The mutated object reference
 * @example
 * ```ts
 * exclude({ a: 1, b: () => {} }, 'Function'); // { a: 1 }
 * ```
 */
export function exclude(obj, ...types) {
    const exclusions = distinct(types.map(item => item.toLowerCase()));
    if (isReference(obj)) { // only works on Objects and Arrays
        const keys = [];
        ownEntries(obj)
            .forEach(([key, value]) => {
            const type = getType(value);
            if (['Object', 'Array'].includes(type)) // recurse into object
                exclude(value, ...exclusions);
            if (isPrimitive(value) && exclusions.includes(type.toLowerCase()))
                keys.push(key);
        });
        if (!isEmpty(keys)) // if any values to be excluded
            omit(obj, ...keys);
    }
    return obj; // return Object reference, even though Object has been mutated
}
export function omit(obj, ...keys) {
    const { type, value } = asType(obj);
    switch (type) {
        case 'Array':
            if (isEmpty(keys)) {
                value.length = 0; // clear Array
                break;
            }
            keys
                .sort()
                .reverse() // remove from end-to-start to preserve indexes
                .forEach(key => value.splice(Number(key), 1)); // remove Array index
            break;
        case 'Object':
            (isEmpty(keys) ? ownKeys(value) : keys) // if no {keys}, assume all ownKeys
                .forEach(key => Reflect.deleteProperty(value, key));
    }
    return value; // return Object reference, even though Object has been mutated
}
/**
 * Removes all own properties from an object or array.
 *
 * @param obj - The object or array to purge
 * @returns The mutated object reference
 * @example
 * ```ts
 * purge({ a: 1 }); // {}
 * ```
 */
export function purge(obj) {
    return omit(obj);
}
/**
 * Resets an object by purging all its existing own properties and replacing them
 * with the properties from another object.
 *
 * @param orig - The original object to reset
 * @param obj - The object containing the new properties
 * @returns The mutated original object reference
 * @example
 * ```ts
 * reset(target, { newProp: 1 });
 * ```
 */
export function reset(orig, obj) {
    return Object.assign(purge(orig), { ...obj });
}
/**
 * Returns a new object containing all 'own' and 'inherited' enumerable properties
 * from the prototype chain of the provided object.
 *
 * @param json - The object to extract properties from
 * @returns A plain object containing the flattened properties
 * @example
 * ```ts
 * const flat = allObject(myInstance);
 * ```
 */
export function allObject(json) {
    return Object.fromEntries(ownEntries(json, true));
}
/**
 * Creates a new object and shadow-copies all own-descriptors (including getters/setters)
 * from the source object.
 *
 * @param source - The object to copy descriptors from
 * @returns A new object with identical descriptors
 * @example
 * ```ts
 * const clone = allDescriptors(source);
 * ```
 */
export const allDescriptors = (source) => {
    return Object.defineProperties({}, Object.getOwnPropertyDescriptors(source));
};
/**
 * Retrieves a distinct array of 'getter' names from an object and its prototype chain.
 *
 * @param obj - The object to inspect
 * @returns An array of property keys that have getter functions
 * @example
 * ```ts
 * const getters = getAccessors(myInstance);
 * ```
 */
export const getAccessors = (obj = {}) => {
    return ownAccessors(obj, 'get');
};
/**
 * Retrieves a distinct array of 'setter' names from an object and its prototype chain.
 *
 * @param obj - The object to inspect
 * @returns An array of property keys that have setter functions
 * @example
 * ```ts
 * const setters = setAccessors(myInstance);
 * ```
 */
export const setAccessors = (obj = {}) => {
    return ownAccessors(obj, 'set');
};
const ownAccessors = (obj = {}, type) => {
    const keys = [];
    const limit = 50;
    let depth = 0;
    // 1. Walk the Instance Prototype chain (for instance accessors)
    let proto = obj.prototype || Object.getPrototypeOf(obj);
    while (proto && proto !== Object.prototype && ++depth < limit) {
        const descriptors = Object.getOwnPropertyDescriptors(proto);
        Reflect.ownKeys(descriptors).forEach(key => {
            if (isFunction(descriptors[key][type]))
                keys.push(key);
        });
        proto = Object.getPrototypeOf(proto);
    }
    // 2. Walk the Constructor chain (for static accessors)
    let constructor = isFunction(obj) ? obj : obj.constructor;
    depth = 0;
    while (constructor && constructor !== Function.prototype && constructor !== Object.prototype && ++depth < limit) {
        const descriptors = Object.getOwnPropertyDescriptors(constructor);
        Reflect.ownKeys(descriptors).forEach(key => {
            if (isFunction(descriptors[key][type]))
                keys.push(key);
        });
        constructor = Object.getPrototypeOf(constructor);
    }
    return distinct(keys);
};
/**
 * Defines a lazy method on a prototype that reifies (shadows) itself upon first access.
 * This allows heavy logic to be deferred while maintaining a clean, synchronous public API.
 *
 * @param target - The prototype or object to define the method on
 * @param key - The method name
 * @param factory - A function returning the actual method implementation
 * @example
 * ```ts
 * lazyMethod(MyClass.prototype, 'heavy', () => function() { return 42; });
 * ```
 */
export function lazyMethod(target, key, factory) {
    Object.defineProperty(target, key, {
        configurable: true,
        enumerable: false, // methods are usually non-enumerable
        get() {
            const impl = factory.call(this);
            if (Reflect.isExtensible(this)) {
                Object.defineProperty(this, key, {
                    value: impl,
                    writable: true,
                    configurable: true,
                    enumerable: false
                });
            }
            return impl;
        }
    });
}
